import {FC, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import DataTable, {TableColumn} from 'react-data-table-component';
import {InView} from 'react-intersection-observer';
import {createPortal} from 'react-dom';
import {toRem} from 'shared/helpers';
import {useResize} from 'app/lib/hooks/useResize';

import {TableWrapper} from './styles';
import {HivesRow} from './types';
import {useHivesTable} from 'features/hives/lib/useHivesTable';
import {HivesEditHeader} from './HivesEditHeader';
import {HivesSettingsCell} from './HivesSettingsCell';
import {HivesMobileTable} from './HivesMobileTable';
import {HiveMarkCell} from 'features/hives/ui/hivesTable/HiveMarkCell';
import {HiveHapinessCell} from 'features/hives/ui/hivesTable/HiveHapinessCell.tsx';
import {HiveDeviceCell} from 'features/hives/ui/hivesTable/HiveDeviceCell.tsx';
import {HiveStatesCell} from 'features/hives/ui/hivesTable/HiveStatesCell.tsx';
import {formatDistanceToNowStrict} from 'date-fns';
import {ru} from 'date-fns/locale';

function isScrollable(el: Element): boolean {
  const cs = window.getComputedStyle(el as HTMLElement);
  const oy = cs.overflowY;
  const canScroll = oy === 'auto' || oy === 'scroll';
  const hasScroll = (el as HTMLElement).scrollHeight > (el as HTMLElement).clientHeight + 1;
  return canScroll && hasScroll;
}

function findDeepestScrollable(root: HTMLElement | null): HTMLElement | null {
  if (!root) return null;
  let found: HTMLElement | null = null;
  const walk = (node: Element) => {
    Array.from(node.children).forEach(walk);
    if (isScrollable(node)) {
      found = node as HTMLElement;
    }
  };
  walk(root);
  if (found) return found;
  const firstChild = root.firstElementChild as HTMLElement | null;
  if (firstChild) return firstChild;
  return root;
}

function findLargestContentNode(scrollRoot: HTMLElement): HTMLElement {
  let best: HTMLElement = scrollRoot;
  let bestHeight = scrollRoot.scrollHeight;
  const stack: Element[] = Array.from(scrollRoot.children);
  while (stack.length) {
    const el = stack.pop() as HTMLElement;
    if (!el) continue;
    const h = el.scrollHeight;
    if (h >= bestHeight) {
      best = el;
      bestHeight = h;
    }
    stack.push(...Array.from(el.children));
  }
  return best;
}

function hasViewportScroll(): boolean {
  const doc = document.documentElement;
  return doc.scrollHeight > window.innerHeight + 1;
}

export const HivesTable: FC = () => {
  const {isScreenLg} = useResize();
  const {
    rows,
    isLoading,
    totalCount,
    conditionalRowStyles,
    customStyles,
    activeCols,
    colItems,
    onActiveColsChange,
    onRowClick,
    onInfiniteScroll,
  } = useHivesTable();

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const [contentNode, setContentNode] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!isScreenLg) {
      setScrollRoot(null);
      setContentNode(null);
      return;
    }
    const root = findDeepestScrollable(wrapperRef.current);
    setScrollRoot(root);
    if (root) {
      const content = findLargestContentNode(root);
      setContentNode(content || root);
    } else {
      setContentNode(null);
    }
  }, [isScreenLg, rows.length]);

  const baseColumns = useMemo<Record<string, TableColumn<HivesRow>>>(
    () => ({
      number: {name: 'Номер улья', selector: (r) => r.number, width: toRem(100), sortable: true},
      year: {
        name: 'Год матки',
        selector: (r) => r.uterus?.year ?? '',
        width: toRem(96),
        sortable: true,
      },
      mark: {
        name: 'Метка',
        cell: (r) => <HiveMarkCell color={r.uterus?.color ?? 0} number={r.uterus?.mark ?? 0} />,
        selector: (r) => r.uterus?.mark ?? 0,
        width: toRem(72),
        sortable: true,
      },
      states: {
        name: 'Состояние',
        selector: (r) => r.states?.length ?? 0,
        cell: (r) => <HiveStatesCell states={r.states} />,
        grow: 2,
        minWidth: toRem(300),
        sortable: true,
      },
      breed: {
        name: 'Порода',
        selector: (r) => r.uterus?.breed ?? '',
        grow: 1,
        minWidth: toRem(140),
        sortable: true,
      },
      frames: {name: 'Рамки', selector: (r) => r.frames, width: toRem(100), sortable: true},
      strength: {name: 'Сила', selector: (r) => r.strength, width: toRem(100), sortable: true},
      brood: {name: 'Расплод', selector: (r) => r.brood, width: toRem(100), sortable: true},
      happiness: {
        name: 'Индекс счастья',
        selector: (r) => r?.happiness ?? 0,
        cell: (r) => <HiveHapinessCell value={r.happiness} />,
        width: toRem(140),
        sortable: true,
      },
      devices: {
        name: 'Устройства',
        selector: (r) => {
          console.log(r);
          return r?.devices ?? 0;
        },
        cell: (r) => <HiveDeviceCell count={r.devices} />,
        width: toRem(120),
        sortable: true,
      },
      updatedAt: {
        name: 'Обновлено',
        selector: (row) => new Date(row.updatedAt).getTime(),
        cell: (row) => {
          return formatDistanceToNowStrict(new Date(row.updatedAt), {
            locale: ru,
            addSuffix: true,
          });
        },
        width: toRem(140),
        sortable: true,
      },
    }),
    []
  );

  const settingsCol: TableColumn<HivesRow> = useMemo(
    () => ({
      name: <HivesEditHeader onChange={onActiveColsChange} items={colItems} />,
      cell: (r) => <HivesSettingsCell id={r.id + ''} isDeleted={!!r.deletedAt} />,
      width: toRem(40),
      compact: true,
      id: 'settings-cell',
      right: true,
    }),
    [colItems, onActiveColsChange]
  );

  const columns = useMemo<TableColumn<HivesRow>[]>(() => {
    const selected = (
      activeCols?.length ? activeCols : Object.keys(baseColumns)
    ) as (keyof typeof baseColumns)[];
    const cols = selected.map((key) => baseColumns[key]).filter(Boolean);
    cols.push(settingsCol);
    return cols;
  }, [activeCols, baseColumns, settingsCol]);

  useEffect(() => {
    if (!isScreenLg) return;
    if (!scrollRoot || isLoading) return;
    if (typeof totalCount === 'number' && rows.length >= totalCount) return;

    let tries = 0;
    const maxTries = 5;

    const fillIfNeeded = () => {
      const needMore = scrollRoot.scrollHeight <= scrollRoot.clientHeight + 1;
      if (needMore && tries < maxTries) {
        tries += 1;
        onInfiniteScroll(true);
        requestAnimationFrame(() => setTimeout(fillIfNeeded, 0));
      }
    };

    const id = requestAnimationFrame(() => setTimeout(fillIfNeeded, 0));
    return () => cancelAnimationFrame(id);
  }, [isScreenLg, scrollRoot, rows.length, isLoading, totalCount, onInfiniteScroll]);

  const mobileAutoInProgress = useRef(false);

  useEffect(() => {
    if (!isScreenLg && !isLoading) {
      mobileAutoInProgress.current = false;
    }
  }, [isScreenLg, isLoading]);

  useEffect(() => {
    if (isScreenLg) return;
    if (isLoading) return;
    if (typeof totalCount === 'number' && rows.length >= totalCount) {
      mobileAutoInProgress.current = false;
      return;
    }

    const needMore = !hasViewportScroll();
    if (!needMore) {
      mobileAutoInProgress.current = false;
      return;
    }

    if (mobileAutoInProgress.current) return;

    mobileAutoInProgress.current = true;
    onInfiniteScroll(true);
  }, [isScreenLg, rows.length, isLoading, totalCount, onInfiniteScroll]);

  return (
    <TableWrapper ref={wrapperRef}>
      {isScreenLg ? (
        <>
          <DataTable
            columns={columns}
            data={rows}
            noDataComponent={
              <div style={{padding: '20px 0', width: 'max-content'}}>
                Ульи с заданными параметрами не найдены
              </div>
            }
            conditionalRowStyles={conditionalRowStyles}
            customStyles={customStyles}
            onRowClicked={onRowClick}
            progressPending={false}
            responsive
            dense
            fixedHeader
            fixedHeaderScrollHeight="calc(100vh - 220px)"
          />
          {scrollRoot &&
            contentNode &&
            createPortal(
              <div style={{width: '100%', position: 'absolute', bottom: 0}}>
                <InView
                  root={scrollRoot}
                  threshold={0}
                  onChange={(inView) => {
                    if (inView && !isLoading) {
                      if (typeof totalCount === 'number' && rows.length >= totalCount) return;
                      onInfiniteScroll(true);
                    }
                  }}
                >
                  <div style={{width: '100%', height: 1}} />
                </InView>
              </div>,
              contentNode
            )}
        </>
      ) : (
        <>
          <HivesMobileTable rows={rows} onRowClick={onRowClick} />
          <InView
            threshold={0}
            rootMargin="200px 0px 0px 0px"
            onChange={(inView) => {
              if (inView && !isLoading) {
                if (typeof totalCount === 'number' && rows.length >= totalCount) return;
                onInfiniteScroll(true);
              }
            }}
          >
            <div style={{width: '100%', height: 1}} />
          </InView>
        </>
      )}
    </TableWrapper>
  );
};
